import { useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContractCard from "./ContractCard";
import EmptyState from "./EmptyState";
import heartLoading from "../../../assests/heartLoading.gif";
import ReportCard from "./ReportCard";
import {
  ADD_TO_CART,
  REMOVE_FROM_CART,
} from "../../../redux/domainAddToCart/domainAddToCart.type";
import { useFeatureFlag } from "../../../context/FeatureFlagContext";

const ContractList = ({
  activeTab,
  filteredContracts,
  mySubscriptions,
  myContracts,
  reports,
  handleShareReport,
  handleViewReport,
  subscribedTables,
  handleSubscribe,
  handleReportSubscribe,
  handleViewProfile,
  handleViewContract,
  handleViewProduct,
  handleViewAssetDetails,
  handleViewResult,
  handleViewLineage,
  handleLineageTransformation,
  totalCount,
  maxHits,
  // Loading states
  loading,
  searchLoading,
  filterLoading,
  setAssetName,
  selectedTab,
  // Infinite scroll props
  loadMore,
  hasMore,
  isFetchingMore,
}) => {
  const cartItems = useSelector((state) => state.domainAddToCart);
  const dispatch = useDispatch();
  const isAddToCartEnabled = useFeatureFlag("addToCart");

  // ── Loading spinners ──────────────────────────────────────────────────────

  const SearchLoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center flex flex-col items-center justify-center space-y-4">
        <img src={heartLoading} alt="Loading..." style={{ width : 200}}/>
        <div className="space-y-2">
          <p className="text-gray-700 text-xl font-semibold">Searching...</p>
          <p className="text-gray-500 text-base">
            Fetching data based on your search
          </p>
        </div>
      </div>
    </div>
  );

  const FilterLoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center flex flex-col items-center justify-center space-y-4">
        <img src={heartLoading} alt="Loading..." className="h-32 w-32" />
        <div className="space-y-2">
          <p className="text-gray-700 text-xl font-semibold">
            Applying filters...
          </p>
          <p className="text-gray-500 text-base">
            Updating results based on your selection
          </p>
        </div>
      </div>
    </div>
  );

  const GeneralLoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center flex flex-col items-center justify-center space-y-4">
        <img src={heartLoading} alt="Loading..." className="h-32 w-32" />
        <div className="space-y-2">
          <p className="text-gray-700 text-xl font-semibold">Loading...</p>
          <p className="text-gray-500 text-base">
            Hang tight! The resources are loading...
          </p>
        </div>
      </div>
    </div>
  );

  if (searchLoading) return <SearchLoadingSpinner />;
  if (filterLoading) return <FilterLoadingSpinner />;
  if (loading) return <GeneralLoadingSpinner />;

  // ── Select-all helpers ────────────────────────────────────────────────────

  const handleSelectall = (e) => {
    if (e.target.checked) {
      const existingIds = new Set(cartItems.map((item) => item.id));
      const newItems =
        myContracts
          ?.filter((contract) => !existingIds.has(contract.id))
          .map(({ id, name, productName }) => ({ id, name, productName })) ||
        [];
      if (newItems.length > 0) {
        dispatch({ type: ADD_TO_CART, payload: newItems });
      }
    } else {
      const myContractIds = myContracts?.map((c) => c.id) || [];
      dispatch({ type: REMOVE_FROM_CART, payload: myContractIds });
    }
  };

  const selectedAll =
    myContracts?.length > 0 &&
    myContracts.every((contract) =>
      cartItems.some((asset) => asset.id === contract.id)
    );

 
  const ScrollSentinel = () => {
    const sentinelRef = useRef(null);

    const handleIntersect = useCallback(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
          loadMore();
        }
      },
      [hasMore, isFetchingMore]
    );

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return;
      const observer = new IntersectionObserver(handleIntersect, {
        rootMargin: "200px", // start loading 200px before the end
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [handleIntersect]);

    return (
      <>
        {/* Invisible trigger element */}
        <div ref={sentinelRef} style={{ height: 1 }} />

        {isFetchingMore && (
          <div className="flex items-center justify-center py-6 gap-2">
            <img
              src={heartLoading}
              alt="Loading more..."
              className="h-32 w-32 "
            />
            <span className="text-gray-500 text-2xl">Loading more...</span>
          </div>
        )}
        {!hasMore && (
          <div className="text-center py-4 text-[#000] text-sm">
            All {totalCount} items loaded
          </div>
        )}
      </>
    );
  };

  // ── Tab renders ───────────────────────────────────────────────────────────

  return (
    <div className="contract-list">

      {/* ── ALL tab ── */}
      {activeTab === "all" &&
        (filteredContracts && filteredContracts.length > 0 ? (
          <>
            <div className="flex justify-between mb-3">
              <div className="flex items-center gap-4">
                {selectedTab === "assets" && isAddToCartEnabled && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="select-all"
                      className="h-4 rounded border-gray-300 text-[#bb1ccc] focus:ring-[#bb1ccc]"
                      onChange={handleSelectall}
                      checked={selectedAll}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-md text-gray-800"
                    >
                      Select All on Page
                    </label>
                  </div>
                )}
                <div className="text-md text-gray-600">
                  {filteredContracts.length} of {totalCount} items loaded
                </div>
              </div>
            </div>

            {filteredContracts.map((contract) => (
              <ContractCard
                key={contract.id}
                contract={contract}
                isSubscribed={subscribedTables.includes(contract.id)}
                onSubscribe={() => handleSubscribe(contract)}
                onReportSubscribe={() => handleReportSubscribe(contract)}
                onViewProfile={() => handleViewProfile(contract)}
                onViewContract={() => handleViewContract(contract)}
                onViewProduct={() => handleViewProduct(contract)}
                onViewAssetDetails={() => handleViewAssetDetails(contract)}
                onViewResult={() => handleViewResult(contract)}
                setAssetName={setAssetName}
                onViewLineage={() => handleViewLineage(contract)}
                onLineageTransformation={() =>
                  handleLineageTransformation(contract)
                }
                activeTab={activeTab}
                selectedAssetList={cartItems}
                setSelectedAssetList={null}
                maxHits={maxHits}
                showCheckbox={true}
                selectedTab={selectedTab}
              />
            ))}

            <ScrollSentinel />
          </>
        ) : (
          <EmptyState
            title="No Assets Found"
            message="Try adjusting your search or filters to find assets."
          />
        ))}

      {/* ── MY SUBSCRIPTIONS tab ── */}
      {activeTab === "mySubscriptions" &&
        (mySubscriptions && mySubscriptions.length > 0 ? (
          <>
            <div className="flex justify-end mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <span className="text-blue-800 font-medium text-sm">
                  {mySubscriptions.length} of {totalCount} items loaded
                </span>
              </div>
            </div>

            {mySubscriptions.map((contract) => (
              <ContractCard
                key={contract.id}
                contract={contract}
                isSubscribed={subscribedTables.includes(contract.id)}
                onSubscribe={() => handleSubscribe(contract)}
                onViewProfile={() => handleViewProfile(contract)}
                onViewContract={() => handleViewContract(contract)}
                onViewProduct={() => handleViewProduct(contract)}
                onViewAssetDetails={() => handleViewAssetDetails(contract)}
                onViewResult={() => handleViewResult(contract)}
                onViewLineage={() => handleViewLineage(contract)}
                activeTab={activeTab}
                maxHits={maxHits}
              />
            ))}

            <ScrollSentinel />
          </>
        ) : (
          <EmptyState
            title="No Subscriptions Yet"
            message="You haven't subscribed to any data contracts yet."
          />
        ))}

      {/* ── MY CONTRACTS tab ── */}
      {activeTab === "myContracts" &&
        (myContracts && myContracts.length > 0 ? (
          <>
            <div className="flex justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="text-md">
                  {myContracts.length} of {totalCount} items loaded
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <span className="text-blue-800 font-medium text-sm">
                  {totalCount} results found
                </span>
              </div>
            </div>

            {myContracts.map((contract) => (
              <ContractCard
                key={contract.id}
                contract={contract}
                isSubscribed={subscribedTables.includes(contract.id)}
                onSubscribe={() => handleSubscribe(contract)}
                onViewProfile={() => handleViewProfile(contract)}
                onViewContract={() => handleViewContract(contract)}
                onViewProduct={() => handleViewProduct(contract)}
                onViewAssetDetails={() => handleViewAssetDetails(contract)}
                onViewResult={() => handleViewResult(contract)}
                onViewLineage={() => handleViewLineage(contract)}
                activeTab={activeTab}
                maxHits={maxHits}
              />
            ))}

            <ScrollSentinel />
          </>
        ) : (
          <EmptyState
            title="No Assets Found"
            message="You don't have any Assets."
          />
        ))}

      {/* ── REPORTS tab ── */}
      {activeTab === "reports" &&
        (reports && reports.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {reports.map((report) => (
              <ReportCard
                key={report.data_asset_id}
                report={report}
                onShareReport={handleShareReport}
                onViewReport={handleViewReport}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No Reports Available"
            message="No reports have been generated yet."
          />
        ))}
    </div>
  );
};

export default ContractList;
